'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
    LuSun,
    LuCloud,
    LuCloudSun,
    LuCloudRain,
    LuCloudDrizzle,
    LuCloudSnow,
    LuCloudLightning,
    LuCloudFog,
} from 'react-icons/lu';
import { t } from '@/i18n/translations';

/* A single fact cell. Renders an optional second line (`sub`, e.g. distance to
 * the airport) and becomes a link to `href` when one is provided. */
function FactCell({ f }) {
    const content = (
        <>
            <span className="k">{f.k}</span>
            <span className="v">{f.v}</span>
            {f.sub && <span className="sub">{f.sub}</span>}
        </>
    );
    if (f.href) {
        return <Link className="cell cell-link" href={f.href}>{content}</Link>;
    }
    return <div className="cell">{content}</div>;
}

function getConditionLabel(conditionMain, conditionCode, lang) {
    const labels = t(lang, 'miscUi').conditions;
    const key = conditionMain?.toLowerCase();
    if (key === 'clouds' && conditionCode >= 801 && conditionCode <= 802) {
        return labels.fewClouds;
    }
    return labels[key] || conditionMain || '';
}

/* ── Weather icons (Lucide via react-icons) ── */
function WeatherIcon({ conditionMain, conditionCode, className }) {
    const key = conditionMain?.toLowerCase();
    const iconClass = className || 'w-10 h-10';

    let Icon = LuSun;
    if (key === 'thunderstorm') Icon = LuCloudLightning;
    else if (key === 'drizzle') Icon = LuCloudDrizzle;
    else if (key === 'rain') Icon = LuCloudRain;
    else if (key === 'snow') Icon = LuCloudSnow;
    else if (['mist', 'smoke', 'haze', 'dust', 'fog', 'sand', 'ash', 'squall', 'tornado'].includes(key)) Icon = LuCloudFog;
    else if (key === 'clear') Icon = LuSun;
    else if (key === 'clouds' && conditionCode >= 801 && conditionCode <= 802) Icon = LuCloudSun;
    else if (key === 'clouds') Icon = LuCloud;

    return <Icon className={iconClass} aria-hidden="true" />;
}

/* ── Short weekday labels (index = Date.getDay(), 0 = Sunday) ── */
function forecastWeekday(dt, tzOffset, lang) {
    const day = new Date((dt + (tzOffset || 0)) * 1000).getUTCDay();
    return t(lang, 'miscUi').weekdays[day];
}

/* ── Current local time for a city, derived from its IANA timezone ── */
function readLocalTime(timeZone, lang) {
    try {
        const now = new Date();
        const time = new Intl.DateTimeFormat(lang === 'ru' ? 'ru-RU' : 'en-US', {
            timeZone, hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(now);
        let gmt = '';
        try {
            const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(now);
            gmt = parts.find((p) => p.type === 'timeZoneName')?.value || '';
        } catch { /* shortOffset unsupported — skip the GMT suffix */ }
        return { time, gmt };
    } catch {
        return null;
    }
}

/* ── Weather-card background: gradient by time of day × condition ── */
function dayPhase(hour) {
    if (hour == null) return 'day';
    if (hour < 6 || hour >= 20) return 'night';
    if (hour < 9) return 'dawn';
    if (hour < 17) return 'day';
    return 'dusk';
}

function conditionGroup(main) {
    const k = (main || '').toLowerCase();
    if (k === 'thunderstorm') return 'storm';
    if (k === 'drizzle' || k === 'rain') return 'rain';
    if (k === 'snow') return 'snow';
    if (['mist', 'smoke', 'haze', 'dust', 'fog', 'sand', 'ash', 'squall', 'tornado'].includes(k)) return 'fog';
    if (k === 'clouds') return 'clouds';
    return 'clear';
}

// All gradients stay dark enough at the top-left (where the temperature sits)
// to keep the white text readable.
const GRADIENTS = {
    clear: {
        dawn: 'linear-gradient(135deg, #1a2a52 0%, #6b4a7a 55%, #d98a5a 100%)',
        day: 'linear-gradient(135deg, #0d3b66 0%, #1e6fa8 55%, #5aa9e0 100%)',
        dusk: 'linear-gradient(135deg, #16223f 0%, #7d4a6a 50%, #e0784a 100%)',
        night: 'linear-gradient(135deg, #080d20 0%, #152044 60%, #243066 100%)',
    },
    clouds: {
        dawn: 'linear-gradient(135deg, #2a2f47 0%, #5a5566 60%, #9a8a8e 100%)',
        day: 'linear-gradient(135deg, #3a4a5a 0%, #5b7287 60%, #8fa3b3 100%)',
        dusk: 'linear-gradient(135deg, #23293a 0%, #544a5a 60%, #8a6a6e 100%)',
        night: 'linear-gradient(135deg, #11151f 0%, #242838 100%)',
    },
    rain: {
        dawn: 'linear-gradient(135deg, #1c2230 0%, #3a4654 100%)',
        day: 'linear-gradient(135deg, #2a3744 0%, #4a5d6e 100%)',
        dusk: 'linear-gradient(135deg, #1a2029 0%, #3a4450 100%)',
        night: 'linear-gradient(135deg, #0c1119 0%, #1e2731 100%)',
    },
    snow: {
        dawn: 'linear-gradient(135deg, #2c3340 0%, #566273 100%)',
        day: 'linear-gradient(135deg, #3c4e61 0%, #6b8195 100%)',
        dusk: 'linear-gradient(135deg, #222a36 0%, #4e5b6e 100%)',
        night: 'linear-gradient(135deg, #1a212c 0%, #3a4654 100%)',
    },
    fog: {
        dawn: 'linear-gradient(135deg, #32363f 0%, #5b626c 100%)',
        day: 'linear-gradient(135deg, #444c56 0%, #69727c 100%)',
        dusk: 'linear-gradient(135deg, #282c34 0%, #565d68 100%)',
        night: 'linear-gradient(135deg, #1e232a 0%, #3a424c 100%)',
    },
    storm: {
        dawn: 'linear-gradient(135deg, #0f0d18 0%, #251f3a 55%, #3a2a52 100%)',
        day: 'linear-gradient(135deg, #14121f 0%, #2a2440 55%, #3d2a52 100%)',
        dusk: 'linear-gradient(135deg, #0f0d18 0%, #251f3a 55%, #3a2a52 100%)',
        night: 'linear-gradient(135deg, #0a0813 0%, #1b1730 55%, #2c2042 100%)',
    },
};

function weatherGradient(conditionMain, hour) {
    const group = GRADIENTS[conditionGroup(conditionMain)] || GRADIENTS.clear;
    return group[dayPhase(hour)] || group.day || group.night;
}

// City-local hour derived from the current-weather timezone offset (seconds).
function localHour(offsetSec) {
    return new Date(Date.now() + (offsetSec || 0) * 1000).getUTCHours();
}

/* Live local-time cell — ticks client-side, so SSR renders a placeholder. */
function LocalTimeCell({ timeFact, lang }) {
    const [clock, setClock] = useState(null);
    useEffect(() => {
        const tick = () => setClock(readLocalTime(timeFact.timeZone, lang));
        tick();
        const id = setInterval(tick, 30000);
        return () => clearInterval(id);
    }, [timeFact.timeZone, lang]);

    const sub = [timeFact.label, clock?.gmt].filter(Boolean).join(' · ');
    return (
        <div className="cell time">
            <span className="k">{timeFact.k}</span>
            <span className="v" suppressHydrationWarning>{clock?.time || '—'}</span>
            {sub && <span className="sub" suppressHydrationWarning>{sub}</span>}
        </div>
    );
}

/* Погода тянется с клиента, а не приходит пропом из серверного рендера.
 *
 * Причина — не UI, а деньги: `fetchWeather` внутри рендера страницы ставил
 * revalidate 6 ч, а Next.js берёт минимум по всему рендеру, поэтому 124 страницы
 * городов регенерировались 4 раза в сутки целиком ради одного блока с
 * температурой. Теперь страница живёт неделю, а погода приходит из
 * /api/weather (тот же 6-часовой кеш, но на CDN). См. src/app/api/weather/route.js.
 *
 * `weather` пропом всё ещё поддерживается — если данные уже есть, запроса не будет.
 */
export default function WeatherBlock({ weather = null, coords = null, lang, cityName, facts = [], timeFact = null }) {
    const ui = t(lang, 'miscUi');

    const lat = coords?.lat;
    const lng = coords?.lng;
    const shouldFetch = weather == null && Number.isFinite(lat) && Number.isFinite(lng);

    const [data, setData] = useState(weather);
    // pending=true только когда запрос реально будет: иначе первый рендер
    // покажет скелет там, где погоды не будет никогда.
    const [pending, setPending] = useState(shouldFetch);

    useEffect(() => {
        if (!shouldFetch) return undefined;
        let cancelled = false;
        (async () => {
            try {
                // Округляем здесь же, а не только на сервере: URL — это ключ
                // CDN-кеша, и он должен быть одинаковым у всех посетителей города.
                const q = `lat=${Math.round(lat * 100) / 100}&lon=${Math.round(lng * 100) / 100}`;
                const res = await fetch(`/api/weather?${q}`);
                const json = res.ok ? await res.json() : null;
                if (!cancelled) setData(json?.weather || null);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setPending(false);
            }
        })();
        return () => { cancelled = true; };
    }, [shouldFetch, lat, lng]);

    // Time-of-day × condition gradient. Initial value uses the "day" phase so
    // SSR and first client render match; the effect refines it to the city's
    // actual local hour after mount.
    const conditionMain = data?.conditionMain;
    const tzOffset = data?.timezoneOffset;
    const [heroBg, setHeroBg] = useState(() => (weather ? weatherGradient(weather.conditionMain, null) : null));
    useEffect(() => {
        if (conditionMain == null && tzOffset == null) return undefined;
        const apply = () => setHeroBg(weatherGradient(conditionMain, localHour(tzOffset)));
        apply();
        const id = setInterval(apply, 600000); // refresh as the day moves
        return () => clearInterval(id);
    }, [conditionMain, tzOffset]);

    if (!data) {
        // Запрос ещё в полёте — держим ту же сетку с плашкой-заглушкой на месте
        // hero-ячейки. Иначе разметка прыгала бы с `facts-only` на полную мозаику.
        if (pending) {
            return (
                <div className="ct-mosaic" aria-busy="true">
                    <div className="cell hero" style={{ backgroundImage: weatherGradient(null, null) }}>
                        <div className="wx-now">
                            <span className="k">{ui.weather.now} · {cityName}</span>
                            <div className="now-row">
                                <span className="big">—</span>
                            </div>
                        </div>
                        {/* Пустая строка прогноза — держит ту же контентную высоту,
                            что и загруженная карточка. Обычно высоту hero задаёт грид
                            (grid-row: span 2 по ячейкам фактов), но у города без
                            фактов высота контентная, и без этой строки был бы скачок. */}
                        <div className="wx-fc" aria-hidden="true">
                            {[0, 1, 2].map((i) => (
                                <div className="fc" key={i}><span className="d">—</span><span className="t">—</span></div>
                            ))}
                        </div>
                    </div>
                    {facts.map((f, i) => (
                        <FactCell f={f} key={i} />
                    ))}
                    {timeFact && <LocalTimeCell timeFact={timeFact} lang={lang} />}
                </div>
            );
        }
        // Погоды не будет (нет координат, нет ключа API, апстрим упал) —
        // сетка только из фактов.
        if (!facts.length && !timeFact) return null;
        return (
            <div className="ct-mosaic facts-only">
                {facts.map((f, i) => (
                    <FactCell f={f} key={i} />
                ))}
                {timeFact && <LocalTimeCell timeFact={timeFact} lang={lang} />}
            </div>
        );
    }

    const conditionLabel = getConditionLabel(data.conditionMain, data.conditionCode, lang);
    const forecast = Array.isArray(data.forecast) ? data.forecast : [];
    const hasForecast = forecast.length > 0;

    // Fallback rows for the right column when no forecast is available.
    const statRows = [
        { d: ui.weather.feels, t: `${data.feelsLike}°` },
        { d: ui.weather.humidity, t: `${data.humidity}%` },
        { d: ui.weather.wind, t: ui.weather.windUnit(data.windSpeed) },
    ];

    // "Gallery · Field" facts + weather mosaic.
    return (
        <div className="ct-mosaic" aria-label={ui.weather.factsAria(cityName)}>
            <div className="cell hero" style={heroBg ? { backgroundImage: heroBg } : undefined}>
                <span className="wx-bg" aria-hidden="true">
                    <WeatherIcon conditionMain={data.conditionMain} conditionCode={data.conditionCode} />
                </span>
                <div className="wx-now">
                    <span className="k">{ui.weather.now} · {cityName}</span>
                    <div className="now-row">
                        <span className="wx-ic">
                            <WeatherIcon conditionMain={data.conditionMain} conditionCode={data.conditionCode} />
                        </span>
                        <span className="big">{data.temp}°</span>
                    </div>
                    <span className="cond">{conditionLabel}</span>
                </div>
                <div className="wx-fc">
                    {hasForecast
                        ? forecast.map((d, i) => (
                            <div className="fc" key={i}>
                                <span className="d">{forecastWeekday(d.dt, data.timezoneOffset, lang)}</span>
                                <span className="t">
                                    {d.max != null ? `${d.max}°` : '—'}{d.min != null ? `/${d.min}°` : ''}
                                </span>
                            </div>
                        ))
                        : statRows.map((s, i) => (
                            <div className="fc" key={i}><span className="d">{s.d}</span><span className="t">{s.t}</span></div>
                        ))}
                </div>
            </div>

            {facts.map((f, i) => (
                <FactCell f={f} key={i} />
            ))}
            {timeFact && <LocalTimeCell timeFact={timeFact} lang={lang} />}
        </div>
    );
}
